import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Button, Card } from '@/ui/primitives'
import { Camp } from '@/ui/form'

export type DateZbor = {
  airline: string | null; flight_number: string | null
  departure_city: string | null; arrival_city: string | null
  departure_time: string | null; arrival_time: string | null
  flight_class: string | null; baggage: string | null
  return_date: string | null; return_flight_number: string | null
  return_departure_time: string | null; return_arrival_time: string | null
}

const GOL: Record<keyof DateZbor, string> = {
  airline: '', flight_number: '', departure_city: '', arrival_city: '',
  departure_time: '', arrival_time: '', flight_class: '', baggage: '',
  return_date: '', return_flight_number: '', return_departure_time: '', return_arrival_time: '',
}

const rezumat = (z: DateZbor) => {
  const dus = [z.airline, z.flight_number].filter(Boolean).join(' ')
  const ruta = [z.departure_city, z.arrival_city].filter(Boolean).join(' → ')
  return [dus, ruta, z.departure_time].filter(Boolean).join(' · ')
}

/** Formularul de zbor al rezervarii. Scrie prin endpointul dedicat (`/zbor`), unde un
 *  camp golit chiar se sterge — pe `PUT /reservations/{id}` nu se putea sterge nimic. */
export function CardZbor({ reservationId, deschisImplicit }:
  { reservationId: number | string; deschisImplicit?: boolean }) {
  const qc = useQueryClient()
  const [deschis, setDeschis] = useState(Boolean(deschisImplicit))
  const [f, setF] = useState<Record<keyof DateZbor, string>>(GOL)
  const [salvat, setSalvat] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)

  const { data } = useQuery({
    queryKey: ['zbor', String(reservationId)],
    queryFn: () => api.get<DateZbor>(`/reservations/${reservationId}/zbor`),
  })

  useEffect(() => {
    if (!data) return
    setF(Object.fromEntries(
      (Object.keys(GOL) as (keyof DateZbor)[]).map((k) => [k, data[k] ?? '']),
    ) as Record<keyof DateZbor, string>)
  }, [data])

  const s = (k: keyof DateZbor) => (v: string) => { setF((x) => ({ ...x, [k]: v })); setSalvat(false) }

  const salveaza = useMutation({
    mutationFn: () => api.put<{ zbor: DateZbor }>(`/reservations/${reservationId}/zbor`, f),
    onSuccess: () => {
      setSalvat(true); setEroare(null)
      qc.invalidateQueries({ queryKey: ['zbor', String(reservationId)] })
      qc.invalidateQueries({ queryKey: ['itinerar', String(reservationId)] })
      qc.invalidateQueries({ queryKey: ['rezervare', String(reservationId)] })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  const areZbor = data ? Object.values(data).some(Boolean) : false

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted">Zbor</h2>
        <button onClick={() => setDeschis((v) => !v)} className="text-[13px] text-accent-ink hover:underline">
          {deschis ? 'inchide' : areZbor ? 'modifica' : 'completeaza'}
        </button>
      </div>

      <Card className="p-5">
        {!deschis ? (
          <p className="text-[14px]">
            {areZbor && data
              ? <>
                  {rezumat(data)}
                  {data.return_flight_number && (
                    <span className="block text-[13px] text-muted">
                      retur {data.return_flight_number}
                      {data.return_date ? ` · ${new Date(data.return_date).toLocaleDateString('ro-RO')}` : ''}
                      {data.return_departure_time ? ` · ${data.return_departure_time}` : ''}
                    </span>
                  )}
                  {data.baggage && <span className="block text-[13px] text-muted">bagaj: {data.baggage}</span>}
                </>
              : <span className="text-muted">
                  Zborul nu e trecut pe rezervare. Completeaza-l aici si apare in programul calatoriei.
                </span>}
          </p>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-[12.5px] text-muted mb-2">Dus</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Camp id="zbor-companie" eticheta="Companie" valoare={f.airline} seteaza={s('airline')} />
                <Camp id="zbor-numar" eticheta="Numar zbor" valoare={f.flight_number} seteaza={s('flight_number')} />
                <Camp id="zbor-de-la" eticheta="Plecare din" valoare={f.departure_city} seteaza={s('departure_city')} />
                <Camp id="zbor-la" eticheta="Soseste in" valoare={f.arrival_city} seteaza={s('arrival_city')} />
                <Camp id="zbor-ora-plecare" eticheta="Ora plecarii" valoare={f.departure_time}
                  seteaza={s('departure_time')} indiciu="ex.: 06:15" />
                <Camp id="zbor-ora-sosire" eticheta="Ora sosirii" valoare={f.arrival_time}
                  seteaza={s('arrival_time')} />
              </div>
            </div>

            <div>
              <p className="text-[12.5px] text-muted mb-2">Retur</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Camp id="retur-data" eticheta="Data returului" tip="date" valoare={f.return_date}
                  seteaza={s('return_date')} />
                <Camp id="retur-numar" eticheta="Numar zbor retur" valoare={f.return_flight_number}
                  seteaza={s('return_flight_number')} />
                <Camp id="retur-ora-plecare" eticheta="Ora plecarii" valoare={f.return_departure_time}
                  seteaza={s('return_departure_time')} />
                <Camp id="retur-ora-sosire" eticheta="Ora sosirii" valoare={f.return_arrival_time}
                  seteaza={s('return_arrival_time')} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Camp eticheta="Clasa" valoare={f.flight_class} seteaza={s('flight_class')} />
              <Camp eticheta="Bagaj" valoare={f.baggage} seteaza={s('baggage')}
                indiciu="ex.: 1 x 20 kg cala + 8 kg cabina" />
            </div>

            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={() => { setEroare(null); salveaza.mutate() }}
                disabled={salveaza.isPending}>
                {salveaza.isPending ? 'Se salveaza…' : 'Salveaza zborul'}
              </Button>
              <Button variant="quiet" onClick={() => setDeschis(false)}>Inchide</Button>
              {salvat && <span className="text-[13px] text-good">Salvat</span>}
              {eroare && <span className="text-[13px] text-bad">{eroare}</span>}
            </div>
            <p className="text-[12px] text-muted">
              Un camp lasat gol se sterge de pe rezervare. Orele se scriu ca text (06:15),
              asa cum sunt pe bilet.
            </p>
          </div>
        )}
      </Card>
    </div>
  )
}
